import React, { useContext, useEffect, useMemo, useState } from 'react';
import {Form, Table, Button, Space, Row, Typography, Switch, Modal} from 'antd';
import {
  HolderOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  CloseCircleFilled
} from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DateRangeSelector from './dashboard/DateRangeSelector.jsx';
import {useLocation} from "react-router-dom";
import {getModelList, updateModel, getModelSwitchLog} from "../services/modelService.js";
import moment from "moment";
import dayjs from "dayjs";
import logIcon from "../assets/log.svg";
const { Text } = Typography;

const RowContext = React.createContext({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const DragRow = props => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key']
  });
  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };
  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const ModelList = () => {
  const location = useLocation();
  const [form] = Form.useForm();
  const [data, setData] = useState({ firstSteps: [], secondSteps: []});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(location.state?.filters || {});
  const [switchLogsVisible, setSwitchLogsVisible] = useState(false);
  const [logData, setLogData] = useState({ rows: [], total: 0 });
  const [logPagination, setLogPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const logColumns = [
    {
      title: 'Switch Time',
      dataIndex: 'createdAt',
      render: (createdAt ) => {
        const time = createdAt ? moment(createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'
        return <Text>{time}</Text>;
      }
    },
    {
      title: 'Operator',
      dataIndex: 'createdBy',
    },
    {
      title: 'Step',
      dataIndex: 'step',
      render: (step) => {
        if (step === 1 ) return 'First';
        if (step === 2 ) return 'Second';
        return '-';
      },
    },
    {
      title: 'From Model',
      dataIndex: 'fromModel',
    },
    {
      title: 'To Model',
      dataIndex: 'toModel',
    },
    {
      title: 'Mode',
      dataIndex: 'mode',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        if (status === 1 ) return <><CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} /> Success </>;
        if (status === 0 ) return <><CloseCircleFilled style={{ color: "red", fontSize: 18 }} /> Failed </>;
        return null;
      },
    },
  ];

  const columns = [
    {
      title: 'Priority',
      dataIndex: 'priority',
      render: priority => <><DragHandle />  {priority}</>
    },
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
    },
    {
      title: 'Added On',
      dataIndex: 'addedOn',
      key: 'addedOn',
      render: (addedOn ) => {
        const time = addedOn ? moment(addedOn).format('YYYY-MM-DD HH:mm:ss') : '-'
        return <Text>{time}</Text>;
      }
    },
    {
      title: 'Activation Time',
      dataIndex: 'activationTime',
      key: 'activationTime',
      render: (activationTime ) => {
        const time = activationTime ? moment(activationTime).format('YYYY-MM-DD HH:mm:ss') : '-'
        return <Text>{time}</Text>;
      }
    },
    {
      title: 'Total Requests',
      dataIndex: 'totalRequests',
    },
    {
      title: 'Total Token Usage',
      dataIndex: 'totalTokenUsage',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        if (status === 1 ) return <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />;
        if (status === 0 ) return <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 18 }} />;
        return null;
      },
    },
    {
      title: 'Edit',
      dataIndex: 'enabled',
      render: (enabled, record) => {
        return (
          <Switch
            checkedChildren="Open"
            unCheckedChildren="Close"
            checked={enabled === 1}
            disabled={enabled === 1}
            loading={loading}
            onChange={async (checked) => {
              const newEnabled = checked ? 1 : 0;
              setLoading(true);
              try {
                await updateModel({ id: record.id, enabled: newEnabled, step: record.step });
                await fetchModelList(filters);
                record.enabled = newEnabled;
              } catch (error) {
                console.error('Failed to update model:', error);
              } finally {
                setLoading(false);
              }
            }}
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (location.state?.filters && Object.keys(location.state.filters).length > 0) {
      form.setFieldsValue(location.state.filters);
    }
  }, [location.state?.filters]);

  useEffect(() => {
    fetchModelList(filters);
  }, []);

  const fetchModelList = async (params = filters) => {
    setLoading(true);
    try {
      const response = await getModelList(params);
      if (response?.code === 200) {
        const resData = response.data;
        if(resData && resData.length > 0) {
          setData({
            firstSteps: resData.filter(item => item.step === 1).map((item, index) => ({
              ...item,
              key: item.id || item.key || `first-${index}`
            })),
            secondSteps: resData.filter(item => item.step === 2).map((item, index) => ({
              ...item,
              key: item.id || item.key || `second-${index}`
            }))
          });
        }
      }
    } catch (error) {
      console.error("Fail to fetch model list:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateModelPriority = async (id, priority) => {
    console.log('Updating model:', id, 'with priority:', priority);
    await updateModel({ id, priority });
    await fetchModelList(filters);
    return Promise.resolve({ code: 200 });
  };

  const handleSearch = (values) => {
    if(values && values.dateRange){
      values.startDate = dayjs(values.dateRange[0]).startOf('day').toISOString();
      values.endDate = dayjs(values.dateRange[1]).endOf('day').toISOString();
      delete values.dateRange;
    }
    setFilters(values);
    fetchModelList(values);
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({});
    fetchModelList({});
  };

  const onDragEndFirst = ({ active, over }) => {
    if (active.id !== over?.id) {
      setData(prevData => {
        const prevState = prevData.firstSteps;
        const activeIndex = prevState.findIndex(record => record.key === active?.id);
        const overIndex = prevState.findIndex(record => record.key === over?.id);
        const newData = arrayMove(prevState, activeIndex, overIndex);

        newData.forEach((item, index) => {
          item.priority = index + 1;
          updateModelPriority(item.id, item.priority);
        });

        return { ...prevData, firstSteps: newData };
      });
    }
  };

  const onDragEndSecond = ({ active, over }) => {
    if (active.id !== over?.id) {
      setData(prevData => {
        const prevState = prevData.secondSteps;
        const activeIndex = prevState.findIndex(record => record.key === active?.id);
        const overIndex = prevState.findIndex(record => record.key === over?.id);
        const newData = arrayMove(prevState, activeIndex, overIndex);

        newData.forEach((item, index) => {
          item.priority = index + 1;
          updateModelPriority(item.id, item.priority);
        });

        return { ...prevData, secondSteps: newData };
      });
    }
  };

  const fetchLog = async (pagination) => {
    try {
      setLoading(true);
      const response = await getModelSwitchLog({
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
      });
      setLogData({
        rows: response.rows || [],
        total: response.total || 0,
      });
      setLoading(false);
    } catch (error) {
      console.error('Fetch log error:', error);
    }
  }
  const handleLogTableChange = (logPagination) => {
    setLogPagination({
      current: logPagination.current,
      pageSize: logPagination.pageSize,
    });
    fetchLog({
      current: logPagination.current,
      pageSize: logPagination.pageSize,
    });
  }

  const handleOpenSwitchLogs = () => {
    setSwitchLogsVisible(true);
    fetchLog(logPagination);
  }

  return (
    <div style={{ padding: 24, background: '#fff' }}>
      <Form form={form} onFinish={handleSearch}>
        <Row gutter={16} align="bottom" style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Form.Item name="dateRange" style={{ marginRight: 30 }}>
            <DateRangeSelector />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">Search</Button>
              <Button onClick={handleReset}>Reset</Button>
            </Space>
          </Form.Item>
          <Form.Item>
            <Button
              type="text"
              icon={
                <img
                  src={logIcon}
                  alt="log"
                  style={{ width: 35, height: 35, marginLeft: 25 }}
                />
              }
              onClick={handleOpenSwitchLogs}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                border: "none",
                background: "none",
                boxShadow: "none",
              }}
            />
          </Form.Item>
        </Row>
      </Form>

      <br/>
      <Text style={{ fontWeight: 'bold' }}>First Step</Text>
      &nbsp;&nbsp;&nbsp;
      <Text>(Input analysis for CTA, risk, organization, and other key metrics)</Text>
      <br/>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEndFirst}>
        <SortableContext items={data.firstSteps.map(i => i.key)} strategy={verticalListSortingStrategy}>
          <Table
            rowKey="id"
            components={{ body: { row: DragRow } }}
            columns={columns}
            dataSource={data.firstSteps}
            loading={loading}
            pagination={false}
          />
        </SortableContext>
      </DndContext>
      <br/>
      <br/>
      <br/>
      <br/>
      <Text style={{ fontWeight: 'bold'}}>Second Step</Text>
      &nbsp;&nbsp;&nbsp;
      <Text>(Generate explanation, suggestion, and formatted output)</Text>
      <br/>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEndSecond}>
        <SortableContext items={data.secondSteps.map(i => i.key)} strategy={verticalListSortingStrategy}>
          <Table
            rowKey="id"
            components={{ body: { row: DragRow } }}
            columns={columns}
            dataSource={data.secondSteps}
            loading={loading}
            pagination={false}
          />
        </SortableContext>
      </DndContext>

      <Modal
        title='Switch Logs'
        open={switchLogsVisible}
        onCancel={() => setSwitchLogsVisible(false)}
        footer={null}
        width={1250}
      >
        <Table
          rowKey="id"
          columns={logColumns}
          dataSource={logData.rows}
          loading={loading}
          pagination={{
            total: logData.total,
            current: logPagination.current,
            pageSize: logPagination.pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${total} in total`,
          }}
          scroll={{ x: "max-content" }}
          onChange={handleLogTableChange}
        />
      </Modal>
    </div>
  );
};

export default ModelList;
